import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, Truck, PackageCheck } from "lucide-react";

const STAGE_ICONS = {
  received: PackageCheck,
  in_production: Loader2,
  shipped: Truck,
  delivered: CheckCircle2,
};

export default function OrderStatus() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("order_id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (oid, em) => {
    if (!oid.trim() || !em.trim()) {
      setError("Please enter your Order # and email.");
      return;
    }
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await fetch(`/api/payments/order-status?order_id=${encodeURIComponent(oid.trim())}&email=${encodeURIComponent(em.trim())}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === "string" ? err.detail : "We couldn't find an order with those details.");
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (params.get("order_id") && params.get("email")) {
      lookup(params.get("order_id"), params.get("email"));
    }
  }, [params, lookup]);

  const stageIndex = data ? data.timeline.findIndex((s) => s.key === data.current_stage) : -1;
  const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

  return (
    <div className="min-h-screen px-6 py-16" style={{ background: "#0a0a0f", fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="order-status-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl mb-2" style={{ color: "#e8c87a", letterSpacing: "1px" }}>Track My Order</h1>
          <p className="text-sm" style={{ color: "rgba(201,184,150,0.6)", fontFamily: "'Raleway', sans-serif" }}>The Book of Colorz · Where Colorz Unite Us</p>
        </div>

        {/* Lookup form */}
        <div className="rounded-2xl p-6 mb-8" style={{ background: "#100d18", border: "1px solid rgba(212,162,84,0.2)" }}>
          <input
            data-testid="lookup-order-id"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Order # (from your confirmation email)"
            className="w-full mb-3 rounded-lg px-4 py-3 text-sm"
            style={{ background: "#0a0a0f", border: "1px solid rgba(212,162,84,0.18)", color: "#f5efe6", fontFamily: "'Raleway', sans-serif" }}
          />
          <input
            data-testid="lookup-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email used at checkout"
            className="w-full mb-4 rounded-lg px-4 py-3 text-sm"
            style={{ background: "#0a0a0f", border: "1px solid rgba(212,162,84,0.18)", color: "#f5efe6", fontFamily: "'Raleway', sans-serif" }}
          />
          <button
            data-testid="lookup-submit-btn"
            onClick={() => lookup(orderId, email)}
            disabled={loading}
            className="w-full py-3 rounded-full text-sm font-medium"
            style={{ background: "linear-gradient(135deg,#e8c87a,#d4a254)", color: "#1a1525", fontFamily: "'Raleway', sans-serif", opacity: loading ? 0.6 : 1 }}
          >
            {loading ? "Looking up..." : "Track My Order"}
          </button>
          {error && <p data-testid="lookup-error" className="text-sm mt-3 text-center" style={{ color: "#e07a5f", fontFamily: "'Raleway', sans-serif" }}>{error}</p>}
        </div>

        {/* Result */}
        {data && (
          <div data-testid="order-status-result" className="rounded-2xl p-6" style={{ background: "#100d18", border: "1px solid rgba(212,162,84,0.2)" }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs" style={{ color: "#d4a254", letterSpacing: "2px", fontFamily: "'Raleway', sans-serif" }}>ORDER #{data.order_no}</span>
              <span className="text-xs" style={{ color: data.payment_status === "paid" ? "#8fbf8f" : "rgba(201,184,150,0.6)", fontFamily: "'Raleway', sans-serif" }}>
                {data.payment_status === "paid" ? "Paid" : "Awaiting payment"}
              </span>
            </div>
            <p className="text-lg mb-5" style={{ color: "#e8c87a" }}>{data.package_name}</p>

            {/* Timeline */}
            <div className="space-y-0 mb-6">
              {data.timeline.map((s, i) => {
                const done = i < stageIndex;
                const active = i === stageIndex;
                const Icon = active && s.key === "in_production" ? Loader2 : (STAGE_ICONS[s.key] || Circle);
                const color = done ? "#8fbf8f" : active ? "#e8c87a" : "rgba(201,184,150,0.35)";
                return (
                  <div key={s.key} className="flex items-start gap-3" data-testid={`timeline-${s.key}`}>
                    <div className="flex flex-col items-center">
                      <Icon size={20} style={{ color }} className={active && s.key === "in_production" ? "animate-spin" : ""} />
                      {i < data.timeline.length - 1 && <div style={{ width: 2, height: 26, background: done ? "#8fbf8f" : "rgba(201,184,150,0.15)" }} />}
                    </div>
                    <div className="pb-4">
                      <p className="text-sm" style={{ color, fontFamily: "'Raleway', sans-serif", fontWeight: active ? 600 : 400 }}>{s.label}</p>
                      {active && s.key === "received" && <p className="text-xs" style={{ color: "rgba(201,184,150,0.5)", fontFamily: "'Raleway', sans-serif" }}>Expected delivery ~10–14 days from order.</p>}
                      {active && s.key === "shipped" && data.tracking && (
                        <a href={data.tracking.url || "#"} className="text-xs" style={{ color: "#d4a254", fontFamily: "'Raleway', sans-serif" }}>
                          Track: {data.tracking.carrier || "carrier"} {data.tracking.id || ""}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="pt-4 space-y-2" style={{ borderTop: "1px solid rgba(212,162,84,0.15)", fontFamily: "'Raleway', sans-serif" }}>
              <div className="flex justify-between text-sm" style={{ color: "rgba(201,184,150,0.7)" }}>
                <span>{data.package_name} × {data.quantity}</span><span>{money(data.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm" style={{ color: "rgba(201,184,150,0.7)" }}>
                <span>Shipping — {data.shipping_label}</span><span>{Number(data.shipping_cost) === 0 ? "FREE" : money(data.shipping_cost)}</span>
              </div>
              {Number(data.tax_amount) > 0 && (
                <div className="flex justify-between text-sm" style={{ color: "rgba(201,184,150,0.7)" }} data-testid="order-status-tax">
                  <span>Sales Tax</span><span>{money(data.tax_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-base pt-1" style={{ color: "#e8c87a" }}>
                <span>Total</span><span>{money(data.total)}</span>
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-xs mt-8" style={{ color: "rgba(201,184,150,0.4)", fontFamily: "'Raleway', sans-serif" }}>
          Need help? <a href="mailto:support@bookofcolorz.com" style={{ color: "#d4a254" }}>support@bookofcolorz.com</a>
        </p>
        <p className="text-center text-xs mt-3" style={{ fontFamily: "'Raleway', sans-serif" }}>
          <a href="/book-of-colorz" data-testid="status-back-link" style={{ color: "rgba(201,184,150,0.6)" }}>← Return to The Book of Colorz</a>
        </p>
      </div>
    </div>
  );
}
